import React from "react";
import "./css/PrivacyPolicy.css";

const TermsAndConditions = () => {
  return (
    <div className="privacy-container">
      <header className="privacy-hero">
        <div className="overlay">
          <h1>Terms &amp; Conditions</h1>
          <p>Last Updated: October 17, 2025</p>
        </div>
      </header>

      <main className="privacy-content">
        {/* Intro */}
        <section>
          <p>
            Welcome to <strong>Web Solution USA</strong> ("we," "our," "us").
            These Terms &amp; Conditions ("Terms") govern your use of our website{" "}
            <a
              href="https://www.websolutionusa.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.websolutionusa.com
            </a>{" "}
            and the purchase or use of our web design, hosting, and digital
            marketing services (collectively, the "Services"). By accessing our
            website or purchasing any plan, you agree to be bound by these
            Terms. If you do not agree, you must not use our website or
            Services.
          </p>
        </section>

        {/* 1. Services Overview */}
        <section>
          <h2>1. Services Overview</h2>
          <p>
            Web Solution USA provides professional website design, hosting, and
            online business solutions for small and medium-sized businesses.
          </p>
          <p>Our service plans include, but are not limited to, the following:</p>
          <ul>
            <li>Website design and hosting</li>
            <li>SEO optimization</li>
            <li>E-commerce setup</li>
            <li>Business integrations (Google, Facebook, etc.)</li>
            <li>Email and marketing automation</li>
          </ul>
          <p>
            The details of the available plans and their inclusions are listed
            on our Pricing page.
          </p>
        </section>

        {/* 2. Pricing & Plans */}
        <section>
          <h2>2. Pricing &amp; Plans</h2>
          <p>
            All prices are listed in U.S. Dollars (USD) and may be updated from
            time to time. Any changes to pricing will not affect existing
            subscriptions until the next renewal term.
          </p>

          <h3>Starter Plan – $16.99/mo (Monthly) or $12.99/mo (Yearly)</h3>
          <ul>
            <li>✔ Custom Domain</li>
            <li>✔ Website Hosting</li>
            <li>✔ 1 Professional Email</li>
            <li>✔ Website Ready in 7 Days</li>
            <li>✔ Mobile Responsive Design</li>
            <li>✔ 5 Web Pages</li>
            <li>✔ Contact / Lead Form</li>
            <li>✔ On-Page SEO</li>
            <li>+ $79 one-time setup pack (non-refundable)</li>
          </ul>

          <h3>Business Plan – $39.99/mo (Monthly) or 29.99/mo (Yearly)</h3>
          <ul>
            <li>✔ Everything in Starter Plan</li>
            <li>✔ Advanced SEO Optimization</li>
            <li>✔ 2 Professional Emails</li>
            <li>✔ Booking / Enquiry Forms</li>
            <li>✔ Invoicing System</li>
            <li>✔ Team Management Tools</li>
            <li>✔ Calendar Integration</li>
            <li>✔ Google Business Integration</li>
            <li>✔ Ongoing Website Management (up to 5 pages)</li>
            <li>+ $79 one-time setup pack (non-refundable)</li>
          </ul>

          <h3>Enterprise Plan – $99.99/mo (Monthly) or $79.99/mo (Yearly)</h3>
          <ul>
            <li>✔ Everything in Business Plan</li>
            <li>✔ Payment Gateway Integration</li>
            <li>✔ 2000 Words of Written Content</li>
            <li>✔ SEO Boost Campaign</li>
            <li>✔ Facebook &amp; Google Ads Management</li>
            <li>✔ SMS &amp; Email Marketing Automation</li>
            <li>✔ 3rd Party App Integrations</li>
            <li>✔ Live Chat Support</li>
            <li>✔ E-commerce Store Setup (up to 500 products)</li>
            <li>+ $199 one-time Enterprise startup fee (non-refundable)</li>
          </ul>
        </section>

        {/* 3. Payment Terms */}
        <section>
          <h2>3. Payment Terms</h2>
          <p>
            Payments are processed on a recurring monthly or annual basis,
            depending on the plan you select. Setup or startup fees are charged
            once during checkout and are non-refundable. By submitting your
            payment information, you authorize us and our payment processors to
            charge the applicable fees to your selected payment method.
          </p>
          <p>
            If a payment fails or is declined, we may suspend or terminate your
            access to the Services until payment is successfully completed.
          </p>
        </section>

        {/* 4. Delivery Timeline */}
        <section>
          <h2>4. Delivery Timeline</h2>
          <p>We value fast delivery and transparency in timelines:</p>
          <ul>
            <li>
              <strong>First Draft:</strong> Delivered within 2 working days of
              receiving your project information.
            </li>
            <li>
              <strong>Final Website Delivery:</strong> Within 7 working days,
              provided all required content (text, images, business information)
              is received from you on time.
            </li>
          </ul>
          <p>
            Delays in providing required materials, approvals, or feedback may
            extend the delivery timelines. We are not responsible for delays
            caused by incomplete or late client submissions.
          </p>
        </section>

        {/* 5. Refund Policy – FULL CONTENT ADDED */}
        <section>
          <h2>5. Refund Policy</h2>

          <p>
            At <strong>Web Solution USA</strong>, we are committed to delivering
            high-quality web design, website launch packages, and digital
            services to help businesses grow online. This Refund Policy explains
            how refunds are handled for our products and services.
          </p>

          {/* 1. No Refund on Startup Fees */}
          <h3>1. No Refund on Startup Fees</h3>
          <p>
            All startup fees, setup charges, onboarding fees, domain fees,
            hosting fees, or any third-party costs paid at the beginning of a
            project are <strong>non-refundable under any circumstances</strong>.
          </p>

          <p>This includes (but is not limited to):</p>
          <ul>
            <li>Website setup or onboarding fees</li>
            <li>Custom domain registration fees</li>
            <li>Hosting activation</li>
            <li>Email setup fees</li>
            <li>Third-party tools or licenses purchased for your project</li>
          </ul>

          <p>
            Once the project has started and resources have been allocated,
            these fees cannot be reversed.
          </p>

          {/* 2. 30-Day Refund Policy */}
          <h3>2. 30-Day Refund Policy</h3>
          <p>
            For all refundable service packages, Web Solution USA offers a{" "}
            <strong>30-day refund policy</strong>.
          </p>

          <h4>Eligibility Requirements:</h4>
          <p>To qualify for a refund (within 30 days):</p>
          <ul>
            <li>You must submit a written request within 30 days of your purchase.</li>
            <li>
              You must have provided all required content, access, and
              cooperation during the project.
            </li>
            <li>
              We must not have completed more than 50% of the agreed project
              scope.
            </li>
            <li>
              Refunds apply only to service fees, not startup fees or third-party
              purchases.
            </li>
          </ul>

          <h4>Refunds will not be granted for:</h4>
          <ul>
            <li>
              Delays caused by lack of response or missing content from your side
            </li>
            <li>Change of mind after designs or development has begun</li>
            <li>Completed or delivered services</li>
            <li>
              Domain, hosting, email, plugins, software, or other third-party
              fees
            </li>
          </ul>

          {/* 3. How to Request a Refund */}
          <h3>3. How to Request a Refund</h3>
          <p>
            To request a refund, please email us at{" "}
            <a href="mailto:support@websolutionusa.com">
              support@websolutionusa.com
            </a>{" "}
            with:
          </p>
          <ul>
            <li>Full name</li>
            <li>Business name</li>
            <li>Invoice or order number</li>
            <li>Reason for your refund request</li>
          </ul>
          <p>
            Our team will review your request and respond within 5–7 business
            days. If approved, refunds will be processed via the original
            payment method within 7–10 business days.
          </p>

          {/* 4. Cancellations */}
          <h3>4. Cancellations</h3>
          <p>
            You may cancel any ongoing subscription or recurring service at any
            time. However, cancellation does not entitle you to a refund for
            charges already billed or services already delivered.
          </p>

          {/* 5. Chargebacks */}
          <h3>5. Chargebacks</h3>
          <p>If a chargeback is initiated without contacting us first:</p>
          <ul>
            <li>Your service may be paused immediately</li>
            <li>All delivered work and licenses may be suspended</li>
            <li>
              We reserve the right to dispute the chargeback and recover service
              costs
            </li>
          </ul>

          
        </section>

        {/* 6. Client Responsibilities */}
        <section>
          <h2>6. Client Responsibilities</h2>
          <p>
            To ensure the timely and successful completion of your project, you
            agree to:
          </p>
          <ul>
            <li>
              Provide all required information, text, images, branding assets,
              and other materials promptly.
            </li>
            <li>
              Review drafts and provide clear feedback within a reasonable
              timeframe.
            </li>
            <li>Maintain valid contact details and payment information.</li>
            <li>
              Ensure that any content provided by you does not infringe on the
              rights of third parties or violate any applicable laws.
            </li>
          </ul>
          <p>
            Failure to provide necessary inputs may delay or pause the project
            timeline. In such cases, we are not responsible for missed
            deadlines.
          </p>
        </section>

        {/* 7. Intellectual Property */}
        <section>
          <h2>7. Intellectual Property</h2>
          <p>
            All website content, design, and materials created by Web Solution
            USA remain the property of the company until full payment is
            received for the applicable Services. Upon final payment, ownership
            of the completed website design and content (excluding licensed
            software, plugins, templates, stock images, or other third-party
            assets) will be transferred to you.
          </p>
          <p>
            You are granted a non-exclusive, non-transferable license to use any
            third-party tools or assets incorporated into your website, subject
            to the terms of the respective third-party providers.
          </p>
        </section>

        {/* 8. Limitation of Liability */}
        <section>
          <h2>8. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, Web Solution USA is not
            liable for:
          </p>
          <ul>
            <li>
              Loss of data due to user negligence, misuse, or third-party
              hosting issues.
            </li>
            <li>
              Website downtime caused by client-side modifications, expired
              domain renewals, or changes made by third-party providers.
            </li>
            <li>
              Any indirect, incidental, consequential, special, or punitive
              damages arising from the use or inability to use our Services,
              even if we have been advised of the possibility of such damages.
            </li>
          </ul>
          <p>
            In any event, our total liability for any claim arising out of or
            relating to these Terms or the Services will be limited to the total
            amount paid by you to us in the three (3) months preceding the event
            giving rise to the claim.
          </p>
        </section>

        {/* 9. Modifications & Updates */}
        <section>
          <h2>9. Modifications &amp; Updates</h2>
          <p>
            We may revise these Terms from time to time. Updated Terms will be
            posted on this page with a new "Last Updated" date. Any material
            changes may also be communicated via email or through a notice on
            our website.
          </p>
          <p>
            Your continued use of our website or Services after any changes
            become effective constitutes your acceptance of the revised Terms.
            If you do not agree to the updated Terms, you must stop using the
            Services.
          </p>
        </section>

        {/* 10. Governing Law */}
        <section>
          <h2>10. Governing Law</h2>
          <p>
            These Terms are governed by and construed in accordance with the
            laws of the State of Delaware, without regard to its conflict of law
            principles. You agree to submit to the exclusive jurisdiction of the
            state and federal courts located in Delaware for the resolution of
            any disputes arising out of or relating to these Terms or the
            Services.
          </p>
        </section>

        {/* 11. Contact Information */}
        <section>
          <h2>11. Contact Information</h2>
          <address>
            <strong>Web Solution USA</strong>
            <br />
            📍 8 The Green #16439, Dover, DE 19901
            <br />
            📧{" "}
            <a href="mailto:sales@websolutionusa.com">
              sales@websolutionusa.com
            </a>
            <br />
            📧{" "}
            <a href="mailto:support@websolutionusa.com">
              support@websolutionusa.com
            </a>
            <br />
            📞 +1 (302) 208 6030
            <br />
            🌐{" "}
            <a
              href="https://www.websolutionusa.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.websolutionusa.com
            </a>
          </address>
        </section>
      </main>
    </div>
  );
};

export default TermsAndConditions;
